import { BOSS_RADIUS } from '../ai/boss';

/**
 * S06's "arena gating" (PLAN-CITY §5). A LOGICAL trigger radius, not a
 * physical wall — deliberately, for two reasons: (1) this session builds on
 * S01's gray Astoria block, not the real C04/S09 Astoria block, which will
 * have its own actual "construction pocket" geometry for Gridlock's lair
 * (PLAN-CITY §5's S10 line) — a new hard-wall arena here would just be
 * thrown away once that lands; (2) every new physical collider in this
 * block has cost a review round in the past (S01's ramp/wall bugs, S05's
 * camera-vs-small-room bug) — a soft trigger has none of that surface area.
 * "Gating" here means: the fight only starts once the player commits to
 * approaching (not visible-from-across-the-map aggro), and retreating far
 * enough away mid-fight resets it rather than letting the boss be worn down
 * via hit-and-run kiting across the whole 60x60m block. Flagged as a
 * deliberate scope call, not hidden — a future Wave C session building the
 * real lair may want a genuine physical gate.
 */
export type EncounterState = 'dormant' | 'active' | 'cleared';

export interface ArenaSpec {
  centerX: number;
  centerZ: number;
  /** Crossing INTO this radius (from `dormant`) starts the fight. Ignored (kept only as a documentation fallback) once `pocket` is set — see that field. */
  radius: number;
  /** Extra distance PAST `radius` the player must retreat before an in-progress fight resets — prevents the fight flickering on/off right at the boundary during ordinary combat repositioning. Reused as the padding margin around `pocket` for the same purpose when `pocket` is set. */
  resetMargin: number;
  /**
   * PLAN-CITY3 §4 K-1(c): "den boundary becomes the physical pocket
   * rectangle, not a radius" — when set, `isInsideEntryRadius`/
   * `isOutsideResetRadius` test this rectangle instead of the logical
   * `radius` circle above. The rectangle should match the real physical
   * boundary (barrier fence + scaffold flanks), not an approximation — see
   * `GRIDLOCK_POCKET` below for the derivation.
   */
  pocket?: { minX: number; maxX: number; minZ: number; maxZ: number };
}

/**
 * Center chosen in the open street strip south of Building C/D (z −38..−26,
 * the full width between the boundary wall and the buildings' own south
 * edge — see layout.ts's `boundaryWalls()`/`BUILDINGS`), comfortably clear
 * of every existing habitat zone and collider (checked against
 * ai/habitats.ts's own areas and world/layout.ts's BoxColliders before
 * picking this spot — nearest habitat, `street_curbs`, ends at z=−22, a
 * clean 5m+ gap from this arena's own northern extent). Radius 5.5m keeps
 * the logical circle inside that ~12m-deep strip with real margin on both
 * the boundary-wall side and the building side; the shockwave's own hit
 * radius (see boss.ts's SHOCKWAVE_RADIUS constant, wired in main.ts) is
 * smaller still, so there's genuine room to dodge to the arena's rim.
 */
export const GRIDLOCK_ARENA: ArenaSpec = {
  centerX: 0,
  centerZ: -32,
  radius: 5.5,
  resetMargin: 3,
  // K-1(c): the pocket's own real boundary — the barrier-fence lines sit at
  // z=-26.7 (north) and z=-37.7 (south, see GRIDLOCK_BARRIER_NORTH/SOUTH
  // below), and the scaffold flanks (world/layout.ts's SCAFFOLD_COLLIDERS)
  // begin at x=±7.75 — matching the wreck-placement comment's own "every box
  // stays inside x∈(-7.75,7.75)" derivation. This rectangle IS the
  // "physical pocket," not an approximation of the old 5.5m logical circle.
  pocket: { minX: -7.75, maxX: 7.75, minZ: -37.7, maxZ: -26.7 },
};

/**
 * T3-5 (PLAN-CITY2 §2, from Karan's G2 playtest: "a boss is a higher-value
 * drop and shouldn't even respawn that fast"): an INTERIM cadence — the S10
 * `'cleared'`->`'dormant'` bridge below used to resurrect the boss the
 * INSTANT the player retreated past `isOutsideResetRadius`, with no timer
 * at all. Wave D's real den rules (PLAN-CITY2 §4, "boss respawn = leave den
 * + timer") will replace this with something den-specific; until then, a
 * flat 5-minute wait on top of the existing retreat requirement is the
 * plan's own explicitly-named stopgap.
 */
const LAIR_RESPAWN_DELAY = 300;

export class BossArena {
  state: EncounterState = 'dormant';
  /** T3-5: counts down from `LAIR_RESPAWN_DELAY` while `state==='cleared'` — the boss may only resurrect once this reaches 0 AND the player is outside the reset radius (main.ts's own 'cleared' branch checks both). */
  private clearedTimer = 0;

  constructor(private readonly spec: ArenaSpec) {}

  get center(): { x: number; z: number } {
    return { x: this.spec.centerX, z: this.spec.centerZ };
  }

  get radius(): number {
    return this.spec.radius;
  }

  private distanceFromCenter(x: number, z: number): number {
    return Math.hypot(x - this.spec.centerX, z - this.spec.centerZ);
  }

  /** K-1(c): tests `spec.pocket` (the physical boundary rectangle) when set, falling back to the logical `radius` circle otherwise — see `ArenaSpec.pocket`'s own comment. */
  isInsideEntryRadius(x: number, z: number): boolean {
    const p = this.spec.pocket;
    if (p) return x >= p.minX && x <= p.maxX && z >= p.minZ && z <= p.maxZ;
    return this.distanceFromCenter(x, z) <= this.spec.radius;
  }

  /** True once the player has retreated far enough past the entry boundary that an in-progress fight should reset. */
  isOutsideResetRadius(x: number, z: number): boolean {
    const p = this.spec.pocket;
    if (p) {
      const m = this.spec.resetMargin;
      return x < p.minX - m || x > p.maxX + m || z < p.minZ - m || z > p.maxZ + m;
    }
    return this.distanceFromCenter(x, z) > this.spec.radius + this.spec.resetMargin;
  }

  activate(): void {
    this.state = 'active';
  }

  /** Mid-fight retreat, or any other "un-fight" this back to dormant so re-entering starts fresh (BossArena itself doesn't restore the boss's own HP — main.ts calls `Boss.resetEncounter()` alongside this). */
  reset(): void {
    this.state = 'dormant';
  }

  clear(): void {
    this.state = 'cleared';
    this.clearedTimer = LAIR_RESPAWN_DELAY;
  }

  /** T3-5: advance the post-clear lair timer — a no-op outside `'cleared'` (mirrors `reset()`/`activate()` already being no-ops on an inapplicable state elsewhere in this file's own call sites). */
  tickClearedTimer(dt: number): void {
    if (this.state === 'cleared' && this.clearedTimer > 0) this.clearedTimer -= dt;
  }

  /** T3-5: true once the interim lair timer has fully elapsed — main.ts's own 'cleared' branch ANDs this with `isOutsideResetRadius` before resurrecting the boss. */
  get clearedTimerDone(): boolean {
    return this.clearedTimer <= 0;
  }
}

// ============================================================================
// Wave D (PLAN-CITY2 §4) — D-4's shared den rule, generalized: "respawn only
// once the player has LEFT (the den's own `insideX` flag reads false) AND a
// timer has elapsed since defeat." `BossArena` above ALREADY satisfies this
// for Gridlock (`isOutsideResetRadius` IS "left," `clearedTimerDone` IS "the
// timer" — re-read together, they were never actually "elapsed time
// regardless of presence": the boss can never resurrect while the player is
// still standing inside the reset radius, since `isOutsideResetRadius` gates
// every 'cleared'->'dormant' transition unconditionally). D-2's barrier fence
// now gives "left" a real physical meaning (walked back out through the one
// entrance gap) instead of a purely logical trigger-radius retreat — no
// change to `BossArena`'s own code was needed for that to become true.
//
// `DenArena` is the SAME state machine, generalized for the two dens that
// have a real `insideX` boolean (world/denLair.ts's `insideLair`, world/
// denSewer.ts's `insideSewer`) instead of a radius — `activate()`/`reset()`
// are called directly from an `insideX` transition rather than a distance
// check, and `clear()`/`tickClearedTimer()`/`clearedTimerDone` are BYTE-
// IDENTICAL to `BossArena`'s own (deliberately: two classes, not a shared
// base, matching this file's own established "duplicate the small shared
// bit rather than force a premature merge" call for RatKingBoss vs Boss).
// ============================================================================
export type DenEncounterState = 'dormant' | 'active' | 'cleared';

export class DenArena {
  state: DenEncounterState = 'dormant';
  private clearedTimer = 0;

  constructor(private readonly respawnDelay: number) {}

  activate(): void {
    this.state = 'active';
  }

  /** The player left mid-fight (walked back out the den's own entrance) — un-fight back to dormant so re-entering starts fresh. Mirrors `BossArena.reset()`. */
  reset(): void {
    this.state = 'dormant';
  }

  clear(): void {
    this.state = 'cleared';
    this.clearedTimer = this.respawnDelay;
  }

  tickClearedTimer(dt: number): void {
    if (this.state === 'cleared' && this.clearedTimer > 0) this.clearedTimer -= dt;
  }

  get clearedTimerDone(): boolean {
    return this.clearedTimer <= 0;
  }
}

/** D-4: first-clear bonus loot + the leave+timer respawn cadence, shared by all three dens. Sized against the existing per-kill economy (loot/droptable.ts: rat_king itself pays 8-12 coins/kill, sewer_elite 4-6) — a "found this den" bonus reads as meaningfully more than one kill's worth without dwarfing the game's existing coin scale. */
export const DEN_FIRST_CLEAR_RATKING = { coins: 100, scrap: 15 };
export const DEN_FIRST_CLEAR_GRIDLOCK = { coins: 100, scrap: 15 };
export const DEN_FIRST_CLEAR_SEWER = { coins: 40, scrap: 6 };
/** How long after a kill a den's boss/elite may resurrect, once the player has ALSO left — same order of magnitude as T3-5's old flat 300s, per-den since a small elite den (sewer) reasonably cycles faster than a boss lair. */
export const DEN_RESPAWN_DELAY_RATKING = 300;
export const DEN_RESPAWN_DELAY_SEWER = 180;

// ============================================================================
// D-2: Gridlock arena wreck dressing (PLAN-CITY2 §4). Ladder derived from
// shipping physics per the brief's own binding spec — walkable decks measured
// live off the delivered `prp_car_wrecked`/`prp_van_wrecked`/`prp_car_stack`
// GLBs (their own `flat_walk_deck` mesh top faces: 1.050 / 2.100 / 2.600,
// matching the brief's table exactly). FOOTPRINT LAW (the brief's one HIGH
// finding): a wreck's walkable deck floor region must share the EXACT same
// XZ footprint as its own body-blocker collider, or the vault system can
// land on a body-top spot with no registered floor region underneath it
// (world/layout.ts's `PARK_PROP_TOPS` precedent) — so each wreck below is
// ONE box (body + decktop share the SAME XZ, deliberately NOT the narrower
// visual deck-mesh footprint, which sits inset from the real chassis edges).
//
// Placement: offset to one side so the wrecks dress the fight rather than
// fill it, and the existing 6 `SCAFFOLD_COLLIDERS` (world/layout.ts) occupy
// the x-flanks at x∈[7.75,12.5]∪[-12.5,-7.75], so every box below stays
// inside x∈(-7.75,7.75) — checked by hand against every scaffold band, zero
// overlap. Chain order (car -> van -> stack): each step's deck-to-deck gap
// sits in [0.3, 1.0]m (satisfies BOTH the brief's ≥0.3m vault-face clearance
// AND its ≤1.0m reachability bound simultaneously, since body and decktop
// now share one footprint).
//
// FIX SESSION (adversarial review, all 3 lenses independently confirmed
// HIGH): the ORIGINAL placement put `wreck_van` (x:0, z:-32.2) directly on
// top of `GRIDLOCK_ARENA`'s own center (0,-32) — Gridlock spawned embedded
// in the van mesh, and the first knockback hit snapped it ~2m across the
// pocket (`Boss.applyKnockbackMovement`'s `resolveHorizontal` call resolving
// the boss's already-overlapping position out to the nearest face).
// `wreck_car` never overlapped the arena center (z=-29.7 vs center z=-32)
// and is untouched. `wreck_van`/`wreck_stack` are re-placed with a genuine
// margin BEYOND BOSS_RADIUS (1.2, ai/boss.ts) from (0,-32) — a first pass
// that cleared the raw threshold by only ~0.1m still let a real knocked-back
// boss drift straight into `wreck_van`'s own expanded collider and get
// clamped there after well under 1m of travel (caught by the S10 knockback-
// drift harness check, not just the load-time assertion below, which only
// checks the boss's REST position, not how much real room it has to move
// before hitting something) — van now clears x=0 by a full 1.0m BEYOND the
// BOSS_RADIUS threshold (box x∈[2.2,7.2]) — clears the arena center via X
// alone, with real margin. `wreck_stack` is moved SOUTH of van (same X,
// continuing the ladder vertically) rather than east of it — an east-of-van
// placement wide enough to also clear the arena center runs the combined
// van+stack width straight into the SCAFFOLD_COLLIDERS east band; moving
// south stays inside the walkable lane instead, and (as a bonus, not a
// requirement — X-clearance alone already suffices) clears the arena
// center by Z too, with real margin. The van->stack gap deliberately stays
// at 0.5m (not pushed to the brief's full 1.0m reachability ceiling to buy
// extra Z-distance from the boss) — a first pass that DID push it to the
// full 1.0m ceiling passed the arena-center-clearance assertion but broke
// the actual vault: at exactly 1.0m the vault probe's own reach
// (VAULT_PROBE_DIST+PLAYER_RADIUS=1.0, controller.ts) has zero slack left,
// and the player falls off van's south edge (leaving its floor region,
// hence `state.grounded`) before the probe ever gets close enough to
// register stack's ledge — caught by the D-2 vault-chain harness check,
// not by the arena-center assertion, which only checks rest positions, not
// real reachability. Ladder gaps re-verified against the final positions:
// car(south face -30.6)->van(north face -30.9) = 0.3m; van(south face
// -32.9)->stack(north face -33.4) = 0.5m — both comfortably inside [0.3,1.0].
// Barrier clearance: van clears both lines (4.2m north, 4.8m south); stack
// clears the north line by 6.7m but only 2.3m south — short of the
// original "≥4m both lines" note (reclaiming that room means moving
// `wreck_car` north, which collides with the D-2 harness's own "entrance
// gap is walkable" check requiring the player reach z<-28 unobstructed) —
// a deliberate, documented trade-off, not silently dropped: 2.3m real
// clearance from the south barrier (itself 5.7m from the boss, well outside
// SHOCKWAVE_RADIUS 4) still leaves genuine dodge room, just less than the
// original note's stated ideal.
// ============================================================================

export interface WreckSpec {
  id: 'wreck_car' | 'wreck_van' | 'wreck_stack';
  file: string;
  /** World placement — unrotated (long axis along X, per the delivered GLB's own authoring). */
  x: number;
  z: number;
  /** Shared body-blocker + decktop-floor XZ footprint (the FOOTPRINT LAW above) — measured from the delivered GLB's own `chassis` mesh, not the narrower `flat_walk_deck` visual. */
  halfWidth: number; // X
  halfDepth: number; // Z
  deckY: number;
}

export const GRIDLOCK_WRECKS: WreckSpec[] = [
  { id: 'wreck_car', file: 'prp_car_wrecked.glb', x: 0, z: -29.7, halfWidth: 2.2, halfDepth: 0.9, deckY: 1.05 },
  { id: 'wreck_van', file: 'prp_van_wrecked.glb', x: 4.7, z: -31.9, halfWidth: 2.5, halfDepth: 1.0, deckY: 2.1 },
  { id: 'wreck_stack', file: 'prp_car_stack.glb', x: 4.7, z: -34.4, halfWidth: 2.3, halfDepth: 1.0, deckY: 2.6 },
];

/**
 * K-1(a)/(c): "the flag must key off standing on any wreck deck, not off
 * height or radius fallout" — an explicit XZ-footprint + deck-height check,
 * independent of distance to the boss or of `VERTICAL_ENGAGEMENT_RANGE`.
 * `deckY` tolerance (0.15) is generous enough to cover normal floor-snap
 * float error while staying well clear of a genuinely different height (the
 * decks are 1.05/2.10/2.60m apart from each other and from street level 0).
 */
export function isOnGridlockWreckDeck(x: number, y: number, z: number): boolean {
  for (const w of GRIDLOCK_WRECKS) {
    if (x < w.x - w.halfWidth || x > w.x + w.halfWidth) continue;
    if (z < w.z - w.halfDepth || z > w.z + w.halfDepth) continue;
    if (Math.abs(y - w.deckY) < 0.15) return true;
  }
  return false;
}

/**
 * Fix-session regression guard (review-requested, both HIGH-finding lenses):
 * fires at module load — a future tuning pass that nudges a wreck back
 * toward the arena center gets a loud, immediate failure instead of a silent
 * re-embed. Checks the raw (unexpanded) box against a BOSS_RADIUS margin —
 * the exact condition that made Gridlock spawn visibly inside `wreck_van`.
 */
(function assertNoWreckContainsArenaCenter(): void {
  const { centerX, centerZ } = GRIDLOCK_ARENA;
  for (const w of GRIDLOCK_WRECKS) {
    const clearsX = Math.abs(centerX - w.x) > w.halfWidth + BOSS_RADIUS;
    const clearsZ = Math.abs(centerZ - w.z) > w.halfDepth + BOSS_RADIUS;
    if (!clearsX && !clearsZ) {
      throw new Error(`GRIDLOCK_WRECKS['${w.id}'] is within BOSS_RADIUS of GRIDLOCK_ARENA's center (${centerX},${centerZ}) — Gridlock would spawn embedded in it.`);
    }
  }
})();

/**
 * Modular barrier line (`prp_barrier_fence.glb`, 3.0m/segment) closing the
 * pocket's two open approach mouths across x∈[-7.75,7.75] (the walkable
 * center lane between the existing scaffold flanks) — the brief's own
 * real job for this prop, since the scaffolds already cover the flanks.
 * NORTH (near the buildings, the natural approach from spawn) keeps a
 * single 3m entrance gap at its center segment; SOUTH (against the
 * boundary wall, a dead end) is fully closed. Both lines sit clear of every
 * `GRIDLOCK_WRECKS` footprint above (checked by hand) and of every
 * `SCAFFOLD_COLLIDERS` band (segments stop at x=±7.5, scaffolds start at
 * x=±7.75 — 0.25m real margin).
 *
 * K-1(d) fix session: the entrance gap moved OFF the street centerline
 * (PLAN-CITY3 §4's own finding — "today the pocket's door is dead-center on
 * the spawn walking line"). The north line now covers x=-3,0,3,6, with the
 * gap at x=-6 instead of the old dead-center x=0 — clear of every wreck
 * (nearest, wreck_car, spans x∈[-2.2,2.2]) and still comfortably inside the
 * west scaffold's own x=-7.75 inner face.
 */
export interface BarrierSegmentSpec {
  x: number;
  z: number;
}
export const GRIDLOCK_BARRIER_NORTH: BarrierSegmentSpec[] = [-3, 0, 3, 6].map((x) => ({ x, z: -26.7 })); // x=-6 segment omitted — the entrance gap, off-center per K-1(d)
export const GRIDLOCK_BARRIER_SOUTH: BarrierSegmentSpec[] = [-6, -3, 0, 3, 6].map((x) => ({ x, z: -37.7 }));
export const GRIDLOCK_BARRIER_HALF_LENGTH = 1.5; // 3.0m segment, along X
export const GRIDLOCK_BARRIER_HALF_THICKNESS = 0.25;
export const GRIDLOCK_BARRIER_HEIGHT = 2.6; // above VAULT_MAX_HEIGHT (1.1) and the discrete jump apex (~1.34) — genuinely blocks, never registered as a floor region

/**
 * K-1(d): "a built entry mouth per L5's walked-threshold rule (visible
 * mouth + crossing toast/sting)" — a low hazard-stripe threshold marker
 * dressing the barrier gap above (x∈[-7.5,-4.5] at z=-26.7), so the pocket
 * reads as a real den entrance rather than an unmarked hole in a fence.
 * `world/block.ts`'s `attachGridlockDressing()` places a real ≥0.15m box
 * here (L3 — never a naked plane). The crossing BEAT itself (toast + boss
 * reveal) is main.ts's own `bossArena.isInsideEntryRadius` transition, not a
 * separate trigger — walking through this mouth is exactly what makes that
 * rectangle check first return true.
 */
export const GRIDLOCK_ENTRY_MOUTH = { x: -6, z: -26.7 };
export const GRIDLOCK_ENTRY_MOUTH_HALF_WIDTH = 1.4; // slightly narrower than the 1.5-half-length gap it dresses, so it reads as sitting inside the opening, not overlapping the flanking fence posts
